import { css, StyleSheet } from 'aphrodite';
import { type FC, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../web/components-v2/Button';
import { FollowUpImportance, type LeadServedSource } from '../../viewModels/queue';
import { useLeadContacts } from '../data/useLeadContacts';
import { useSaveCallNote } from '../data/useSaveCallNote';
import { useScheduleFollowUp } from '../data/useScheduleFollowUp';
import { DealForm } from '../deal';
import { IconCalendarNote, IconFileText, IconPlus } from '../ui/icons';
import { CustomFollowUpModal } from './CustomFollowUpModal';
import { NoteEditor } from './NoteEditor';
import type { FollowUp } from './types';

export type QuickActionsPanelProps = {
	/** Company id for the lead the actions are performed against. */
	companyId: string | undefined;
	/** Source attribution forwarded to the deal create. */
	source: LeadServedSource;
	/** Lead/contact context for the panel header (display only). */
	leadName?: string;
	contactName?: string;
	/** The lead's IANA timezone, forwarded to the deal form's meeting scheduler. */
	leadTimeZone?: string;
	/**
	 * Most-recent deal id for the lead, when present. When set, the deal action
	 * switches to "Update deal" and the `DealForm` mounts in Update mode against
	 * this id — mirrors the legacy FAB menu's CreateDeal/UpdateDeal swap
	 * (`Timeline/index.tsx:236` → `lead.loadDealForm(lastDeal.id)`).
	 */
	lastDealId?: string;
	/** Fired when an action completes so the parent can close the drawer. */
	onActionComplete?: () => void;
};

type ActiveAction = 'menu' | 'note' | 'deal';

/** Maps the modal's spice level to the API's importance enum. */
const IMPORTANCE_MAP: Record<'low' | 'medium' | 'high', FollowUpImportance> = {
	low: FollowUpImportance.Low,
	medium: FollowUpImportance.Medium,
	high: FollowUpImportance.High,
};

export const QuickActionsPanel: FC<QuickActionsPanelProps> = ({
	companyId,
	source,
	leadName,
	contactName,
	leadTimeZone,
	lastDealId,
	onActionComplete,
}) => {
	const [action, setAction] = useState<ActiveAction>('menu');
	const [followUpOpen, setFollowUpOpen] = useState(false);
	const [noteHtml, setNoteHtml] = useState('');

	const saveNote = useSaveCallNote();
	const scheduleFollowUp = useScheduleFollowUp();
	const contactsQuery = useLeadContacts(companyId);

	const subtitleParts = [leadName, contactName].filter((p): p is string => Boolean(p));
	const subtitle = subtitleParts.join(' · ');

	const handleSaveNote = async () => {
		if (!companyId || !noteHtml.trim()) return;
		try {
			await saveNote.mutateAsync({ phoneCallId: 'manual', companyId, contentHtml: noteHtml, isForOutcome: false });
			toast.success('Note saved');
			setNoteHtml('');
			setAction('menu');
			onActionComplete?.();
		} catch (err) {
			// No MutationCache in this app (see packages/main/CLAUDE.md) — surface
			// failures explicitly. Don't echo the raw API message to the rep.
			console.error('Quick add-note failed:', err);
			toast.error('Failed to save note. Please try again.');
		}
	};

	const handleConfirmFollowUp = async (followUp: Extract<FollowUp, { kind: 'scheduled' }>) => {
		if (!companyId) return;
		// Don't schedule until the lead's contacts have loaded — legacy `setFollowUp`
		// attaches the lead's contacts, so submitting before they resolve would
		// silently persist a follow-up with no contact attachments.
		const leadContacts = contactsQuery.data;
		if (!leadContacts) {
			toast.error('Contacts are still loading — try again in a moment.');
			return;
		}
		const contactIds = leadContacts.map(c => c.id).filter((id): id is string => Boolean(id));
		try {
			await scheduleFollowUp.mutateAsync({
				companyId,
				contactIds,
				date: followUp.date,
				name: followUp.name,
				importance: IMPORTANCE_MAP[followUp.importance],
			});
			toast.success('Follow-up scheduled');
			setFollowUpOpen(false);
			onActionComplete?.();
		} catch (err) {
			console.error('Quick schedule-follow-up failed:', err);
			toast.error('Failed to schedule follow-up. Please try again.');
		}
	};

	const renderHeader = (title: string) => (
		<header className={css(styles.head)}>
			<div className={css(styles.headLeft)}>
				{action !== 'menu' && (
					<Button variant='link' size='sm' styles={[styles.backLink]} onClick={() => setAction('menu')}>
						← Back
					</Button>
				)}
				<span className={css(styles.title)}>{title}</span>
				{subtitle && <span className={css(styles.subtitle)}>{subtitle}</span>}
			</div>
		</header>
	);

	if (!companyId) {
		return (
			<div className={css(styles.root)}>
				{renderHeader('Quick Actions')}
				<div className={css(styles.emptyState)} aria-live='polite'>
					Select a lead to add a note, schedule a follow-up, or create a deal.
				</div>
			</div>
		);
	}

	if (action === 'deal') {
		return (
			<div className={css(styles.root)}>
				{renderHeader(lastDealId ? 'Update Deal' : 'Create Deal')}
				<div className={css(styles.body)}>
					<DealForm
						companyId={companyId}
						dealId={lastDealId}
						leadTimeZone={leadTimeZone}
						leadServedSource={source}
						onCancel={() => setAction('menu')}
						onCreated={() => {
							setAction('menu');
							onActionComplete?.();
						}}
					/>
				</div>
			</div>
		);
	}

	if (action === 'note') {
		return (
			<div className={css(styles.root)}>
				{renderHeader('Add Note')}
				<div className={css(styles.noteStep)}>
					<NoteEditor value={noteHtml} onChange={setNoteHtml} placeholder='Add a note…' />
					<div className={css(styles.ctaRow)}>
						<Button variant='outline' onClick={() => setAction('menu')} disabled={saveNote.isPending}>
							Cancel
						</Button>
						<Button onClick={handleSaveNote} disabled={!noteHtml.trim() || saveNote.isPending}>
							{saveNote.isPending ? 'Saving…' : 'Save Note'}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={css(styles.root)}>
			{renderHeader('Quick Actions')}
			<div className={css(styles.menu)}>
				<Button variant='ghost' styles={[styles.actionButton]} onClick={() => setAction('deal')}>
					<IconPlus size={18} color='currentColor' />
					<span>{lastDealId ? 'Update deal' : 'Create a deal'}</span>
				</Button>
				<Button variant='ghost' styles={[styles.actionButton]} onClick={() => setAction('note')}>
					<IconFileText size={18} color='currentColor' />
					<span>Add note</span>
				</Button>
				<Button
					variant='ghost'
					styles={[styles.actionButton]}
					disabled={contactsQuery.isLoading}
					onClick={() => setFollowUpOpen(true)}
				>
					<IconCalendarNote size={18} color='currentColor' />
					<span>Follow-up</span>
				</Button>
			</div>
			<CustomFollowUpModal open={followUpOpen} onOpenChange={setFollowUpOpen} onConfirm={handleConfirmFollowUp} />
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		background: 'var(--bg-elev)',
	},
	head: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
		padding: '14px 18px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
	},
	headLeft: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
	},
	title: {
		fontSize: 13,
		fontWeight: 600,
		color: 'var(--ink)',
	},
	subtitle: {
		fontSize: 11,
		color: 'var(--ink-muted)',
	},
	backLink: {
		alignSelf: 'flex-start',
		background: 'transparent',
		border: 'none',
		color: 'var(--accent)',
		cursor: 'pointer',
		fontSize: 12,
		fontWeight: 500,
		padding: 0,
		marginBottom: 4,
	},
	body: {
		flex: 1,
		overflowY: 'auto',
		padding: 0,
		minHeight: 0,
	},
	menu: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		padding: '14px 18px',
	},
	actionButton: {
		display: 'flex',
		alignItems: 'center',
		// Override the Button ghost variant's centered layout so the icon + label
		// stay left-aligned like the legacy FAB menu.
		justifyContent: 'flex-start',
		gap: 12,
		width: '100%',
		height: 'auto',
		padding: '12px 14px',
		background: 'var(--bg)',
		border: '1px solid var(--border)',
		borderRadius: 8,
		color: 'var(--ink)',
		fontSize: 13,
		fontWeight: 500,
		cursor: 'pointer',
		textAlign: 'left',
		transitionProperty: 'border-color, background-color',
		transitionDuration: '120ms',
		transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
		':hover': {
			borderColor: 'var(--accent)',
		},
	},
	noteStep: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		padding: '14px 18px',
		flex: 1,
		minHeight: 0,
	},
	ctaRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: 12,
	},
	emptyState: {
		padding: '14px 18px',
		color: 'var(--ink-muted)',
		fontSize: 13,
	},
});
